import { datadogRum } from '@datadog/browser-rum';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router';
import {
  AvailableOptions,
  Dashboard,
  StepName,
  Summary,
  getAvailableOptionsForOrder,
  getDashboard,
  getSummary,
  rollBack
} from '|/app/services';
import { useBrand } from '|/brand';
import { setCurrentStepName } from '|/helpers/current-step-tracker';
import { setChatPageContext } from './constants/chat-page-context';
import { getChatSurface } from './constants/chat-surface-mappings';
import { deriveVehicleSummary } from './derive-vehicle-summary';
import { VERIFICATION_STEP_IDS } from './verifications/constants';

const useCheckoutVm = (
  dashboard: Dashboard | null,
  summary: Summary | null,
  setDashboard: (d: Dashboard) => void,
  setSummary: (s: Summary) => void,
  setAvailableOptions: (a: AvailableOptions) => void,
  hasScheduleSlotPreference: () => boolean = () => false,
  clearScheduleSlotPreference: () => void = () => undefined
) => {
  const navigate = useNavigate();
  const { hash, pathname, search } = useLocation();
  const { brand } = useBrand();
  const [activeStepUrl, setActiveStepUrl] = useState<string | null>(null);
  const [exitingStepUrl, setExitingStepUrl] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const exitingTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const prevActiveForExitingRef = useRef<string | null>(null);
  if (prevActiveForExitingRef.current !== activeStepUrl) {
    const prev = prevActiveForExitingRef.current;
    prevActiveForExitingRef.current = activeStepUrl;
    if (prev !== null && activeStepUrl !== null && prev !== activeStepUrl) {
      clearTimeout(exitingTimerRef.current);
      setExitingStepUrl(prev);
    }
  }

  // React Router's history uses `popstate`, which doesn't fire for hash-only
  // URL bar edits — only `hashchange` does. Mirror both sources (router nav
  // and manual URL edits) into a single state the main effect can depend on.
  const [liveHash, setLiveHash] = useState<string>(() =>
    typeof window === 'undefined' ? '' : window.location.hash
  );
  useEffect(() => {
    setLiveHash(hash);
  }, [hash]);
  useEffect(() => {
    const onHashChange = () => {
      setLiveHash(window.location.hash);
    };
    window.addEventListener('hashchange', onHashChange);
    return () => {
      window.removeEventListener('hashchange', onHashChange);
    };
  }, []);

  // Initial data load: /checkout is entered directly (refresh, bookmark, deep
  // link) without going through /next-step, so nothing else has populated
  // state. Ref-guarded one-shot — subsequent state updates from step saves
  // bypass this.
  const initialFetchTriggered = useRef(false);
  useEffect(() => {
    if (dashboard !== null && summary !== null) return;
    if (initialFetchTriggered.current) return;
    initialFetchTriggered.current = true;

    Promise.all([getDashboard(), getSummary(), getAvailableOptionsForOrder()])
      .then(([dash, sum, opts]) => {
        if (dash === null || sum === null) {
          navigate('/vehicle/missing', { replace: true });
          return;
        }
        const { vehicle: { isAvailable = false, id = null } = {} } = sum;
        if (!isAvailable) {
          navigate(`/vehicle/unavailable/${id}`, { replace: true });
          return;
        }
        setDashboard(dash);
        setSummary(sum);
        if (opts !== null) setAvailableOptions(opts);
      })
      .catch(() => void 0);
  }, [dashboard, summary, navigate, setDashboard, setSummary, setAvailableOptions]);

  // Safety: non-treatment users who land on /checkout get bounced back through
  // the canonical loader so they land on the correct step in /steps/*.
  // Treatment users whose section 1 is still incomplete also get bounced.
  useEffect(() => {
    if (summary === null || dashboard === null) return;
    const isInTreatment =
      summary.experiments?.some(e => e.key === 'pp-check-it-out' && e.bucket === 'treatment') ??
      false;
    if (!isInTreatment) {
      navigate('/next-step', { replace: true });
      return;
    }
    const { steps = [] } = dashboard;
    const latestStep = [...steps]
      .sort((a, b) => a.order - b.order)
      .find(step => step.isEnabled && !step.isComplete);
    if (latestStep && latestStep.section !== 2) {
      navigate('/next-step', { replace: true });
    }
  }, [summary, dashboard, navigate]);

  const activeStepUrlRef = useRef(activeStepUrl);

  // Hash is an entry mechanism (deep-link to open a specific fold), not tracked
  // state: resolve it to a step, strip it, then let dashboard routing take over.
  useEffect(() => {
    if (dashboard === null) return;

    const { steps = [] } = dashboard;
    const orderedSteps = steps.sort((a, b) => a.order - b.order);
    const firstIncompleteSection2Step = orderedSteps.find(
      step => step.section === 2 && step.isEnabled && !step.isComplete
    );

    if (!firstIncompleteSection2Step) return;

    if (liveHash) {
      let hashUrl = liveHash.replace(/^#/, '');
      // Strip through the router rather than a raw `history.replaceState`. The
      // raw call left react-router's own location reporting the consumed hash
      // forever, so a second deep-link to the same fold was indistinguishable
      // from the stale one. react-router maintains `history.state.idx` itself
      // on a replace, which is the bookkeeping TXCC-9639 was about — that bug
      // came from a raw replaceState passing `null` state, not a router
      // replace. Use the router's pathname, not window.location's: the router
      // runs under a '/purchase' basename and re-adds it, so a document path
      // would double-prefix.
      navigate({ pathname, search }, { replace: true });

      // iOS native Persona redirects to /steps/drivers-license which maps to
      // this hash — remap to the check-it-out section 2 equivalent.
      if (hashUrl === 'drivers-license') {
        hashUrl = 'buyer-identity';
      }

      if (hashUrl === '_verification') {
        setActiveStepUrl('_verification');
        return;
      }

      const isVerifSubstep = orderedSteps.some(
        step => step.section === 2 && step.url === hashUrl && VERIFICATION_STEP_IDS.has(step.id)
      );
      if (isVerifSubstep) {
        setActiveStepUrl(hashUrl);
        return;
      }

      const hashStep = orderedSteps.find(step => step.section === 2 && step.url === hashUrl);
      if (hashStep) {
        setActiveStepUrl(hashStep.url);
        return;
      }
      // Fall through to default routing if hash didn't match.
    }

    // Stripping the hash above re-runs this effect with no hash; without this
    // guard that pass would overwrite the fold the deep-link just opened.
    if (activeStepUrlRef.current !== null) return;

    // Insurance is rendered on its own treatment-gated route, not in the accordion.
    if (firstIncompleteSection2Step.id === StepName.Insurance) {
      navigate('/steps/insurance-options', { replace: true });
      return;
    }

    // Virtual step routing for Schedule/CaseSubmission
    if (firstIncompleteSection2Step.id === StepName.Schedule) {
      setActiveStepUrl(hasScheduleSlotPreference() ? 'complete-purchase' : 'transfer-select-slot');
      return;
    }
    if (firstIncompleteSection2Step.id === StepName.CaseSubmission) {
      setActiveStepUrl('complete-purchase');
      return;
    }

    // Default routing: collapse verification steps under '_verification'
    if (VERIFICATION_STEP_IDS.has(firstIncompleteSection2Step.id)) {
      setActiveStepUrl('_verification');
    } else {
      setActiveStepUrl(firstIncompleteSection2Step.url);
    }
  }, [dashboard, liveHash, navigate, pathname, search, hasScheduleSlotPreference]);

  activeStepUrlRef.current = activeStepUrl;

  useEffect(() => {
    if (activeStepUrl === null) {
      setCurrentStepName(null);
      return;
    }
    // When the active step is a verification sub-step, write just "verification"
    // so useVerificationsVm can refine to "verification.<sub-step>" without
    // being clobbered by this effect's later run.
    const matchedStep = dashboard?.steps.find(s => s.url === activeStepUrl);
    const isVerifSubStep = matchedStep ? VERIFICATION_STEP_IDS.has(matchedStep.id) : false;
    const analyticStep = isVerifSubStep ? 'verification' : activeStepUrl.replace(/^_/, '');
    setCurrentStepName(analyticStep);
    datadogRum.setGlobalContextProperty('checkoutStep', analyticStep);
    return () => {
      datadogRum.removeGlobalContextProperty('checkoutStep');
    };
  }, [activeStepUrl, dashboard]);

  useEffect(() => {
    if (activeStepUrl === null) return;
    const chatSurface = getChatSurface(activeStepUrl, brand);
    if (!chatSurface) return;
    setChatPageContext(chatSurface);
  }, [activeStepUrl, brand]);

  const handleStepComplete = useCallback(() => {
    setExitingStepUrl(activeStepUrlRef.current);
    setIsRefreshing(true);
    const refreshStart = performance.now();
    const minSpinnerMs = 400;

    Promise.all([getDashboard(), getSummary(), getAvailableOptionsForOrder()])
      .then(([freshDashboard, freshSummary, freshAvailableOptions]) => {
        if (freshDashboard === null || freshSummary === null) return;

        setDashboard(freshDashboard);
        setSummary(freshSummary);
        setAvailableOptions(freshAvailableOptions);
        hasRolledBackRef.current = false;

        const { steps = [] } = freshDashboard;
        const orderedSteps = steps.sort((a, b) => a.order - b.order);
        const nextStep = orderedSteps.find(step => step.isEnabled && !step.isComplete);

        if (!nextStep) return;

        if (nextStep.section === 2) {
          if (nextStep.id === StepName.Insurance) {
            navigate('/steps/insurance-options', { replace: true });
          } else if (VERIFICATION_STEP_IDS.has(nextStep.id)) {
            setActiveStepUrl('_verification');
          } else if (nextStep.id === StepName.Schedule) {
            setActiveStepUrl(
              hasScheduleSlotPreference() ? 'complete-purchase' : 'transfer-select-slot'
            );
          } else if (nextStep.id === StepName.CaseSubmission) {
            setActiveStepUrl('complete-purchase');
          } else {
            setActiveStepUrl(nextStep.url);
          }
        } else {
          navigate('/next-step', { replace: true });
        }
      })
      .catch(() => void 0)
      .finally(() => {
        const elapsed = performance.now() - refreshStart;
        const remaining = Math.max(0, minSpinnerMs - elapsed);
        if (remaining === 0) {
          setIsRefreshing(false);
        } else {
          setTimeout(() => {
            setIsRefreshing(false);
          }, remaining);
        }
      });
  }, [navigate, setDashboard, setSummary, setAvailableOptions, hasScheduleSlotPreference]);

  useEffect(() => {
    if (exitingStepUrl === null) return;
    exitingTimerRef.current = setTimeout(() => {
      setExitingStepUrl(null);
    }, 5000);
    return () => {
      clearTimeout(exitingTimerRef.current);
    };
  }, [exitingStepUrl]);

  const handleToggle = useCallback((url: string) => {
    setActiveStepUrl(url);
  }, []);

  const hasRolledBackRef = useRef(false);

  const ensureInProgress = useCallback(async () => {
    const isOrderPlaced =
      dashboard?.steps.some(s => s.id === StepName.PlaceOrder && s.isComplete) ?? false;
    if (!isOrderPlaced || hasRolledBackRef.current) return;
    hasRolledBackRef.current = true;
    await rollBack({ cancelTransfer: false, keepReviewOrder: true });
    clearScheduleSlotPreference();
  }, [dashboard, clearScheduleSlotPreference]);

  const vehicleSummary = useMemo(() => (summary ? deriveVehicleSummary(summary) : null), [summary]);

  return {
    activeStepUrl,
    exitingStepUrl,
    setActiveStepUrl,
    handleStepComplete,
    handleToggle,
    ensureInProgress,
    isRefreshing,
    vehicleSummary
  };
};

export default useCheckoutVm;
